import type { JSX } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { Breadcrumbs, Link,Typography } from '@mui/material';
import { RoutesNameMap } from 'common/constants/routes';

import { NotFound } from './NotFound';

type IdName = {
    resourceName: string;
    resourceId: string
}

type BreadcrumbsContentProps = {
    linkTo: string,
    value: string,
    areTabsExcludedFromLinks: boolean
}

enum NumberOfTabsShouldBeExcludedFromLinks {
    ONE = 1,
    TWO = 2
}

type StyledBreadcrumbsProps = {
  guidNameMappings: IdName[],
  numberOfTabsToExcludeFromLinks?: number
};

const _areNumberOfTabsShouldBeExcludedFromLinks = (length: number, index: number, tabsNumber: number): boolean => {
    return length > tabsNumber ? index >= length - tabsNumber : true;
};

export const getTabNameFromLink = (pathSegment: string): string | undefined => {
    if(RoutesNameMap.has(pathSegment)) {
        return RoutesNameMap.get(pathSegment);
    }
    return undefined;
};

export function StyledBreadcrumbs({
    guidNameMappings,
    numberOfTabsToExcludeFromLinks = NumberOfTabsShouldBeExcludedFromLinks.TWO
}: Readonly<StyledBreadcrumbsProps>): JSX.Element {
    const location = useLocation();
    const pathNames = location.pathname.split('/').filter(Boolean);

    return (
        <Breadcrumbs data-testid="breadcrumbs" aria-label="breadcrumb" separator="›" sx={{ py: 2 }}>
            {pathNames.map((value, index) => {
                const to = `/${pathNames.slice(0, index + 1).join('/')}`;
                const tabName = getTabNameFromLink(value);

                const idName = guidNameMappings.find(x => x.resourceId === value);
                if (idName) {
                    value = idName.resourceName;
                }
                else if (tabName) {
                    value = tabName;
                } else {
                    return (
                        <div key={to}>
                            <NotFound/>
                        </div>
                    );
                }

                switch(numberOfTabsToExcludeFromLinks){
                    case NumberOfTabsShouldBeExcludedFromLinks.ONE:
                        return _buildBreadcrumbsContent({
                            linkTo: to,
                            value,
                            areTabsExcludedFromLinks: _areNumberOfTabsShouldBeExcludedFromLinks(pathNames.length, index, NumberOfTabsShouldBeExcludedFromLinks.ONE)
                        });
                    case NumberOfTabsShouldBeExcludedFromLinks.TWO:
                    default:
                        return _buildBreadcrumbsContent({
                            linkTo: to,
                            value,
                            areTabsExcludedFromLinks: _areNumberOfTabsShouldBeExcludedFromLinks(pathNames.length, index, NumberOfTabsShouldBeExcludedFromLinks.TWO)
                        });
                }
            })}
        </Breadcrumbs>
    );
}

const _buildBreadcrumbsContent = ({
    linkTo,
    value,
    areTabsExcludedFromLinks
}: BreadcrumbsContentProps): JSX.Element => {
    return areTabsExcludedFromLinks ?
        (
            <Typography color="textPrimary" key={linkTo}>
                {value}
            </Typography>
        ) : (
            <Link color="inherit" component={RouterLink} to={linkTo} key={linkTo}>
                {value}
            </Link>
        );
};
